// src/components/PreviewPanel.tsx
import React, { useState, useMemo, useEffect } from 'react';
import JSZip from 'jszip';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { PreviewFile } from '../utils/extractFiles';

interface Props {
  files: PreviewFile[];
  onClose: () => void;
}

const RUNNABLE = ['html', 'xml', 'svg'];
const CSS_LANGS = ['css'];
const JS_LANGS = ['js', 'javascript', 'jsx'];

function extOf(language: string, filename?: string) {
  if (filename && filename.includes('.')) return filename.split('.').pop()!.toLowerCase();
  return (language || 'txt').toLowerCase();
}

// Builds one combined, runnable HTML document out of however many files were
// generated in this response — the same idea as Claude's multi-file artifact
// preview. If there's an .html file, its <link>/<script src> tags are resolved
// against the other generated files; any CSS/JS files that aren't explicitly
// linked are still injected so nothing generated gets silently dropped.
function buildCombinedDoc(files: PreviewFile[]): string | null {
  const htmlFile = files.find(f => extOf(f.language, f.filename) === 'html' || extOf(f.language, f.filename) === 'xml');
  const cssFiles = files.filter(f => CSS_LANGS.includes(extOf(f.language, f.filename)));
  const jsFiles = files.filter(f => JS_LANGS.includes(extOf(f.language, f.filename)));

  if (!htmlFile) {
    // Phase 2: a lone SVG (e.g. a logo design, no HTML page around it) gets
    // its own minimal wrapper so "Expand" actually shows the rendered image
    // instead of falling through to plain syntax-highlighted text. This is
    // the same content CodeBlock's inline SvgPreview already renders (via
    // DOMPurify) — this path is just for the bigger side-panel view.
    const svgFile = files.find(f => extOf(f.language, f.filename) === 'svg');
    if (svgFile && jsFiles.length === 0 && cssFiles.length === 0) {
      return `<!DOCTYPE html><html><head><style>html,body{margin:0;height:100%;display:flex;align-items:center;justify-content:center;background:#fff;}svg{max-width:90%;max-height:90%;}</style></head><body>${svgFile.code}</body></html>`;
    }

    // No page to anchor to — still let CSS/JS-only responses render in a
    // blank sandboxed shell so "Preview" isn't dead for those cases.
    if (jsFiles.length === 0 && cssFiles.length === 0) return null;
    const style = cssFiles.map(f => `<style>\n${f.code}\n</style>`).join('\n');
    const script = jsFiles.map(f => `<script>\n${f.code}\n</script>`).join('\n');
    return `<!DOCTYPE html><html><head>${style}</head><body>${script}</body></html>`;
  }

  let doc = htmlFile.code;
  const isFragment = !/<html[\s>]/i.test(doc);

  const linkedNames = new Set<string>();
  doc = doc.replace(/<link[^>]+href=["']([^"']+)["'][^>]*>/gi, (tag, href) => {
    const match = cssFiles.find(f => href.endsWith(f.filename) || f.filename.endsWith(href));
    if (match) {
      linkedNames.add(match.filename);
      return `<style>\n${match.code}\n</style>`;
    }
    return tag;
  });
  doc = doc.replace(/<script[^>]+src=["']([^"']+)["'][^>]*><\/script>/gi, (tag, src) => {
    const match = jsFiles.find(f => src.endsWith(f.filename) || f.filename.endsWith(src));
    if (match) {
      linkedNames.add(match.filename);
      return `<script>\n${match.code}\n</script>`;
    }
    return tag;
  });

  // Anything not referenced by an explicit <link>/<script src> still gets
  // injected so a generated stylesheet/script is never silently missing.
  const leftoverCss = cssFiles.filter(f => !linkedNames.has(f.filename));
  const leftoverJs = jsFiles.filter(f => !linkedNames.has(f.filename));
  const extraStyle = leftoverCss.map(f => `<style>\n${f.code}\n</style>`).join('\n');
  const extraScript = leftoverJs.map(f => `<script>\n${f.code}\n</script>`).join('\n');

  if (isFragment) {
    doc = `<!DOCTYPE html><html><head>${extraStyle}</head><body>${doc}${extraScript}</body></html>`;
  } else {
    doc = /<\/head>/i.test(doc) ? doc.replace(/<\/head>/i, `${extraStyle}</head>`) : doc.replace(/<head[^>]*>/i, m => `${m}${extraStyle}`);
    doc = /<\/body>/i.test(doc) ? doc.replace(/<\/body>/i, `${extraScript}</body>`) : doc + extraScript;
  }
  return doc;
}

export default function PreviewPanel({ files, onClose }: Props) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = files[activeIdx] || files[0];
  const combinedDoc = useMemo(() => buildCombinedDoc(files), [files]);
  const [tab, setTab] = useState<'preview' | 'code'>(combinedDoc ? 'preview' : 'code');
  const [copied, setCopied] = useState(false);
  const [zipping, setZipping] = useState(false);

  useEffect(() => {
    // Reset to a sane default tab whenever a fresh set of files is opened
    // (e.g. a new "Open in Preview" click) rather than on every keystroke.
    setTab(combinedDoc ? 'preview' : 'code');
    setActiveIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  const handleCopy = () => {
    navigator.clipboard.writeText(active.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([active.code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = active.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadAll = async () => {
    // Bundles every generated file into one real .zip instead of firing
    // several separate sequential browser downloads (which most browsers
    // also throttle/block as looking like spam after the first couple).
    setZipping(true);
    try {
      const zip = new JSZip();
      files.forEach(f => zip.file(f.filename, f.code));
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'generated-files.zip';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setZipping(false);
    }
  };

  if (!active) return null;

  return (
    <div className="preview-panel">
      <div className="preview-panel-header">
        <div className="preview-tabs">
          {combinedDoc && (
            <button
              className={`preview-tab ${tab === 'preview' ? 'active' : ''}`}
              onClick={() => setTab('preview')}
            >
              Preview
            </button>
          )}
          <button
            className={`preview-tab ${tab === 'code' ? 'active' : ''}`}
            onClick={() => setTab('code')}
          >
            Code
          </button>
        </div>
        <div className="preview-panel-actions">
          <button className="code-block-btn" onClick={handleCopy}>
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            className="code-block-btn"
            onClick={files.length > 1 ? handleDownloadAll : handleDownload}
            disabled={zipping}
          >
            {files.length > 1
              ? (zipping ? 'Zipping…' : `Download all (${files.length})`)
              : 'Download'}
          </button>
          <button className="preview-close" onClick={onClose} title="Close preview">✕</button>
        </div>
      </div>

      {files.length > 1 && tab === 'code' && (
        <div className="file-tabs">
          {files.map((f, i) => (
            <button
              key={f.filename}
              className={`file-tab ${i === activeIdx ? 'active' : ''}`}
              onClick={() => setActiveIdx(i)}
              title={f.filename}
            >
              {f.filename}
            </button>
          ))}
        </div>
      )}

      <div className="preview-panel-body">
        {tab === 'preview' && combinedDoc ? (
          <iframe
            title="output-preview"
            className="preview-iframe"
            srcDoc={combinedDoc}
            sandbox="allow-scripts allow-modals allow-forms"
          />
        ) : (
          <SyntaxHighlighter
            style={oneDark as any}
            language={active.language || 'text'}
            customStyle={{ margin: 0, height: '100%', fontSize: 13.5 }}
            showLineNumbers
          >
            {active.code}
          </SyntaxHighlighter>
        )}
      </div>
    </div>
  );
}